import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-draggable',
  // standalone: true,
  template: `
    <div #root class="draggable-root" (pointerdown)="onPointerDown($event)">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .draggable-root {
        display: inline-block;
        touch-action: none; /* crucial for mobile */
        user-select: none;
        /* cursor: grab; */
        will-change: transform;
      }
      .draggable-root.dragging {
        /* cursor: grabbing; */
      }
    `,
  ],
})
export class DraggableComponent implements OnDestroy {
  /** If true, component will visually move itself (default). */
  @Input() moveSelf = false;

  /** Optional: only start drag if pointerdown is inside this selector (e.g. ".header") */
  @Input() handleSelector?: string;

  /** Emits the delta X (in pixels) since drag start, on every move. */
  @Output() dx = new EventEmitter<number>();

  /** Optional extra: emits both deltas. */
  @Output() delta = new EventEmitter<{ dx: number; dy: number }>();

  /** Emits when drag ends (final deltas). */
  @Output() dragEnd = new EventEmitter<{ dx: number; dy: number }>();

  @ViewChild('root', { static: true }) rootRef!: ElementRef<HTMLElement>;

  private dragging = false;
  private pointerId: number | null = null;

  private startX = 0;
  private startY = 0;

  // current applied translate
  private x = 0;
  private y = 0;

  // translate at drag start
  private baseX = 0;
  private baseY = 0;
  private unlistenMove?: () => void;
  private unlistenUp?: () => void;
  private unlistenCancel?: () => void;

  constructor(private zone: NgZone) {}

  onPointerDown(ev: PointerEvent) {
    const root = this.rootRef.nativeElement;

    if (this.handleSelector) {
      const target = ev.target as HTMLElement | null;
      if (!target?.closest(this.handleSelector)) return;
    }

    // Prevent selecting text / dragging images, etc.
    ev.preventDefault();

    this.dragging = true;
    this.pointerId = ev.pointerId;

    root.classList.add('dragging');
    root.setPointerCapture(ev.pointerId);

    this.startX = ev.clientX;
    this.startY = ev.clientY;

    this.baseX = this.x;
    this.baseY = this.y;

    // Listen on window so moves keep working even if pointer leaves element
    this.zone.runOutsideAngular(() => {
      const onMove = (e: PointerEvent) => this.onPointerMove(e);
      const onUp = (e: PointerEvent) => this.onPointerUp(e);
      const onCancel = (e: PointerEvent) => this.onPointerCancel(e);

      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp, { passive: true });
      window.addEventListener('pointercancel', onCancel, { passive: true });

      this.unlistenMove = () =>
        window.removeEventListener('pointermove', onMove);
      this.unlistenUp = () => window.removeEventListener('pointerup', onUp);
      this.unlistenCancel = () =>
        window.removeEventListener('pointercancel', onCancel);
    });
  }

  private onPointerMove(ev: PointerEvent) {
    if (!this.dragging || this.pointerId !== ev.pointerId) return;

    // If you want to stop page scrolling in some browsers:
    ev.preventDefault();

    const dx = ev.clientX - this.startX;
    const dy = ev.clientY - this.startY;

    // Emit inside Angular so bindings update
    this.zone.run(() => {
      this.dx.emit(dx);
      this.delta.emit({ dx, dy });
    });

    if (this.moveSelf) {
      this.x = this.baseX + dx;
      this.y = this.baseY + dy;
      this.rootRef.nativeElement.style.transform = `translate(${this.x}px, ${this.y}px)`;
    }
  }

  private onPointerUp(ev: PointerEvent) {
    if (!this.dragging || this.pointerId !== ev.pointerId) return;

    const dx = ev.clientX - this.startX;
    const dy = ev.clientY - this.startY;

    this.cleanupPointer();

    this.zone.run(() => this.dragEnd.emit({ dx, dy }));
  }

  private onPointerCancel(_ev: PointerEvent) {
    if (!this.dragging) return;
    this.cleanupPointer();
  }

  private cleanupPointer() {
    const root = this.rootRef.nativeElement;

    this.dragging = false;

    if (this.pointerId != null) {
      try {
        if (root.hasPointerCapture(this.pointerId))
          root.releasePointerCapture(this.pointerId);
      } catch {}
    }

    this.pointerId = null;
    root.classList.remove('dragging');

    this.unlistenMove?.();
    this.unlistenMove = undefined;
    this.unlistenUp?.();
    this.unlistenUp = undefined;
    this.unlistenCancel?.();
    this.unlistenCancel = undefined;
  }

  ngOnDestroy() {
    this.cleanupPointer();
  }
}
